/**
* TabNav - Bottom navigation with stretchy blob pill animation
*/
import { useEffect, useRef, useState } from "react";
import {
  House, ChartPie, Gem, Calendar, ShoppingBag
} from "lucide-react";

const TabNav = ({ activeTab, setActiveTab, theme = "pink" }) => {
  const tabs = [
    { id: "rules",    label: "Home",      icon: House },
    { id: "budget",   label: "Budget",    icon: ChartPie },
    { id: "savings",  label: "Wealth",    icon: Gem },
    { id: "calendar", label: "Calendar",  icon: Calendar },
    { id: "wishlist", label: "Wishlist",  icon: ShoppingBag },
  ];

  const tabRefs = useRef([]);
  const pillRef = useRef(null);
  const prevIndexRef = useRef(null);
  const activeIndex = tabs.findIndex(t => t.id === activeTab);

  const themeColor = {
    pink:  { icon: "#CA287A", text: "#CA287A" },
    blue:  { icon: "#4B3FD8", text: "#4B3FD8" },
    warm:  { icon: "#2d5a3d", text: "#2d5a3d" }
  }[theme] || { icon: "#e879a0", text: "#e879a0" };

  useEffect(() => {
    const el = tabRefs.current[activeIndex];
    const pill = pillRef.current;
    if (!el || !pill) return;

    const parent = el.parentElement;
    const parentRect = parent.getBoundingClientRect();
    const rect = el.getBoundingClientRect();
    const targetLeft = rect.left - parentRect.left;
    const targetWidth = rect.width;
    const prev = prevIndexRef.current;

    if (prev === null) {
      pill.style.transition = "none";
      pill.style.left = targetLeft + "px";
      pill.style.width = targetWidth + "px";
      pill.style.opacity = "1";
      pill.style.transform = "scale(1)";
    } else {
      const prevEl = tabRefs.current[prev];
      const prevRect = prevEl ? prevEl.getBoundingClientRect() : rect;
      const goingRight = activeIndex > prev;

      // Step 1: instantly stretch + scale up like a blob expanding
      pill.style.transition = "none";
      pill.style.transform = "scale(1.0, 1.18)";
      if (goingRight) {
        pill.style.left = (prevRect.left - parentRect.left) + "px";
        pill.style.width = (rect.right - prevRect.left) + "px";
      } else {
        pill.style.left = targetLeft + "px";
        pill.style.width = (prevRect.right - rect.left) + "px";
      }

      // Step 2: spring snap to destination with bounce
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          pill.style.transition = [
            "left 0.3s cubic-bezier(0.34,1.6,0.64,1)",
            "width 0.3s cubic-bezier(0.34,1.6,0.64,1)",
            "transform 0.3s cubic-bezier(0.34,1.6,0.64,1)",
          ].join(", ");
          pill.style.left = targetLeft + "px";
          pill.style.width = targetWidth + "px";
          pill.style.transform = "scale(1)";
        });
      });
    }

    prevIndexRef.current = activeIndex;
  }, [activeIndex]);

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 wy-nav" style={{ background: "rgba(255, 255, 255, 0.72)", backdropFilter: "blur(24px) saturate(180%)", WebkitBackdropFilter: "blur(24px) saturate(180%)", borderTop: "1px solid rgba(255, 255, 255, 0.5)", boxShadow: "0 -1px 0 rgba(0, 0, 0, 0.04), 0 -8px 24px rgba(0, 0, 0, 0.04)" }}>
      <div style={{
        background: "transparent",
        boxShadow: "none"
      }}>
        <div className="flex justify-around items-center px-2 pt-1.5 pb-0 relative">
          {tabs.map((tab, i) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                ref={el => tabRefs.current[i] = el}
                data-testid={`tab-${tab.id}`}
                data-active={isActive ? '1' : '0'}
                onClick={() => setActiveTab(tab.id)}
                className="flex flex-col items-center justify-center min-w-0 flex-1 py-0.5 px-2 relative"
                style={{ zIndex: 1 }}
              >
                <tab.icon style={{
                  width: 20, height: 20, marginBottom: 2,
                  stroke: isActive ? themeColor.icon : "#9ca3af",
                  strokeWidth: isActive ? 2 : 1.5,
                  fill: "none",
                  transition: "stroke 0.2s ease",
                }} />
                <span style={{
                  fontSize: 10,
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? themeColor.text : "#9ca3af",
                  transition: "color 0.2s ease",
                }}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="safe-area-bottom-spacer wy-nav-spacer" style={{ background: "transparent" }} />
    </nav>
  );
};

export default TabNav;
